import { useEffect, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/stores/authStore'

let globalSocket: Socket | null = null
let globalAuthState = false
let socketRefCount = 0

export const useSocket = () => {
  const { isAuthenticated } = useAuthStore()
  // Force re-render when socket connects/disconnects so consumers can attach listeners
  const [connectedSocket, setConnectedSocket] = useState<Socket | null>(globalSocket)

  useEffect(() => {
    // If auth state changed, reconnect (drop old connection first)
    if (globalSocket && globalAuthState !== isAuthenticated) {
      globalSocket.disconnect()
      globalSocket = null
    }

    if (!isAuthenticated) {
      // When auth is lost, tear down if no other component is using the socket.
      if (socketRefCount <= 0 && globalSocket) {
        globalSocket.disconnect()
        globalSocket = null
        globalAuthState = false
        setConnectedSocket(null)
      }
      return
    }

    if (!globalSocket) {
      const s = io((import.meta as any).env.PROD ? window.location.origin : 'http://localhost:3000', {
        // F1.10: Auth is carried via httpOnly cookies; withCredentials sends cookies automatically.
        withCredentials: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      })

      globalSocket = s
      globalAuthState = isAuthenticated

      s.on('connect', () => {
        console.log('Socket connected')
        setConnectedSocket(s)
      })

      s.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason)
        setConnectedSocket(null)
        if (reason === 'io server disconnect') {
          // Server forced disconnect, try reconnect manually
          s.connect()
        }
      })

      s.on('connect_error', (err) => {
        console.log('Socket connect error:', err.message)
        if (err.message.includes('Authentication required') || err.message.includes('Invalid token')) {
          // Don't keep retrying if auth failed
          s.disconnect()
          globalSocket = null
          globalAuthState = false
          setConnectedSocket(null)
        }
      })
    } else {
      // Sync state if socket already exists (e.g. after HMR or re-mount)
      setConnectedSocket(globalSocket.connected ? globalSocket : null)
    }

    socketRefCount++

    return () => {
      socketRefCount--
      // Only tear down the shared socket when the last active consumer unmounts
      if (socketRefCount <= 0 && globalSocket) {
        globalSocket.disconnect()
        globalSocket = null
        globalAuthState = false
        setConnectedSocket(null)
      }
    }
  }, [isAuthenticated])

  return connectedSocket
}

export const getSocket = () => globalSocket
